import React, { FC, useMemo } from 'react';
import {
  Badge,
  Box,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Link,
  Select,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import { Datasources } from '@adflow/types';
import { LuExternalLink } from 'react-icons/lu';
import { PiSparkle } from 'react-icons/pi';

export type CreatomateDataMappingMagicProps = {
  mappingEnabled: boolean;
  onToggleMapping: (val: boolean) => void;
  suggestedDataSources: Datasources.SuggestedDataSources;
  inferredImportConfig?: Datasources.InferredImportDataSourceConfig | null;
  sourceFamilyOptions?: Datasources.ImportDataSourceFamilyOption[];
  selectedSourceFamily?: Datasources.ImportDataSourceFamily | null;
  competitionOptions?: Datasources.ImportDataSourceCompetitionOption[];
  selectedCompetition?: Datasources.ImportDataSourceSportsMomentsCompetition | null;
  selectedStage?: Datasources.SourceType | null;
  onImportOverridesChange?: React.Dispatch<
    React.SetStateAction<Datasources.ImportDataSourceOverrides>
  >;
};

const CreatomateDataMappingMagic: FC<CreatomateDataMappingMagicProps> = ({
  mappingEnabled,
  onToggleMapping,
  suggestedDataSources,
  inferredImportConfig = null,
  sourceFamilyOptions = [],
  selectedSourceFamily = null,
  competitionOptions = [],
  selectedCompetition = null,
  selectedStage = null,
  onImportOverridesChange,
}) => {
  const matchedDataSources = useMemo(
    () => Datasources.summarizeSuggestedDataSources(suggestedDataSources),
    [suggestedDataSources],
  );

  const stageOptions = useMemo(
    () => Datasources.getImportDataSourceStageOptions(inferredImportConfig),
    [inferredImportConfig],
  );

  const familyLabel = inferredImportConfig
    ? Datasources.getImportDataSourceFamilyLabel(inferredImportConfig.sourceFamily)
    : null;

  const configurationDetails = useMemo(
    () =>
      Datasources.getImportDataSourceDisplayDetails(inferredImportConfig, {
        competition: selectedCompetition,
        stage: selectedStage,
      }),
    [inferredImportConfig, selectedCompetition, selectedStage],
  );

  const confirmationMessage = useMemo(
    () =>
      Datasources.getImportDataSourceConfirmationMessage(inferredImportConfig, {
        competition: selectedCompetition,
        stage: selectedStage,
      }),
    [inferredImportConfig, selectedCompetition, selectedStage],
  );

  const showSourceFamilySelector = sourceFamilyOptions.length > 0;
  const showCompetitionSelector =
    inferredImportConfig?.sourceFamily === 'SPORTS_MOMENTS' && competitionOptions.length > 0;
  const competitionLabel = showCompetitionSelector ? 'Sport' : 'Competition';
  const competitionPlaceholder =
    selectedCompetition == null
      ? showCompetitionSelector
        ? 'Choose sport…'
        : 'Choose competition…'
      : undefined;

  return (
    <>
      <Heading size="sm">
        Data Mapping <Icon as={PiSparkle} />
      </Heading>
      {suggestedDataSources.length ? (
        <>
          <Text mb="0">
            We matched <Badge colorScheme="blue">{suggestedDataSources.length}</Badge> potential
            data sources for your creative.
          </Text>
          <Text mt={2} mb="0">
            Source groups:{' '}
            {matchedDataSources.map(({ label, matchCount }, idx) => (
              <React.Fragment key={label}>
                <Badge colorScheme="blue" textTransform="none">
                  {label} ({matchCount} {matchCount === 1 ? 'match' : 'matches'})
                </Badge>
                {idx === matchedDataSources.length - 1 ? '' : ', '}
              </React.Fragment>
            ))}
            .
          </Text>
        </>
      ) : (
        <>
          <Text mb="0">
            We couldn&apos;t find any potential dynamic elements in your creative. Element names
            should follow a{' '}
            <Link
              color="teal.500"
              href="https://marketing-cloud.sportradar.com/support/solutions/articles/77000515734-sportsbooks-integration-manual"
              isExternal
            >
              naming specification <Icon as={LuExternalLink} />
            </Link>{' '}
            to be detected.
          </Text>
        </>
      )}
      {showSourceFamilySelector && (
        <Box mt={3} p={3} borderWidth={1} borderRadius="md" bg="blackAlpha.50">
          <Stack spacing={2}>
            <FormControl>
              <FormLabel fontSize="sm" mb={1}>
                Source
              </FormLabel>
              <Select
                size="sm"
                placeholder={selectedSourceFamily == null ? 'Choose source…' : undefined}
                value={selectedSourceFamily ?? ''}
                onChange={(e) => {
                  const sourceFamily = e.target.value as Datasources.ImportDataSourceFamily | '';
                  onImportOverridesChange?.((prev) => ({
                    ...prev,
                    sourceFamily: sourceFamily || null,
                    competition:
                      sourceFamily &&
                      sourceFamily === selectedSourceFamily &&
                      sourceFamily === 'SPORTS_MOMENTS'
                        ? prev.competition ?? null
                        : null,
                    stage:
                      sourceFamily && sourceFamily === selectedSourceFamily ? prev.stage : null,
                  }));
                }}
              >
                {sourceFamilyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            {selectedSourceFamily == null && (
              <Text fontSize="sm" color="orange.600">
                Choose the source family to keep the detected mapping when the import finishes.
              </Text>
            )}
          </Stack>
        </Box>
      )}
      {inferredImportConfig && familyLabel && (
        <Box mt={3} p={3} borderWidth={1} borderRadius="md" bg="blackAlpha.50">
          <Heading size="sm" mb={2}>
            {familyLabel}
          </Heading>
          <Stack spacing={2}>
            <Text fontSize="sm">
              Detected configuration:{' '}
              {configurationDetails.map(({ key, label, value, isPendingSelection }, idx) => (
                <React.Fragment key={key}>
                  <Badge
                    colorScheme={
                      isPendingSelection ? 'orange' : key === 'market' ? 'green' : 'blue'
                    }
                    textTransform="none"
                  >
                    {label}: {value}
                  </Badge>
                  {idx === configurationDetails.length - 1 ? '' : ' '}
                </React.Fragment>
              ))}
            </Text>
            {confirmationMessage && (
              <Text fontSize="sm" color="orange.600">
                {confirmationMessage}
              </Text>
            )}
            {showCompetitionSelector && (
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>
                  {competitionLabel}
                </FormLabel>
                <Select
                  size="sm"
                  placeholder={competitionPlaceholder}
                  value={selectedCompetition ?? ''}
                  onChange={(e) => {
                    const competition = e.target.value as
                      | Datasources.ImportDataSourceSportsMomentsCompetition
                      | '';
                    onImportOverridesChange?.((prev) => ({
                      ...prev,
                      competition: competition || null,
                      stage: competition && competition === selectedCompetition ? prev.stage : null,
                    }));
                  }}
                >
                  {competitionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
            {stageOptions.length > 0 && (
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>
                  Stage
                </FormLabel>
                <Select
                  size="sm"
                  placeholder={selectedStage == null ? 'Choose stage…' : undefined}
                  value={selectedStage ?? ''}
                  onChange={(e) => {
                    const stage = e.target.value as Datasources.SourceType;
                    if (stage) {
                      onImportOverridesChange?.((prev) => ({ ...prev, stage }));
                    }
                  }}
                >
                  {stageOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Box>
      )}
      <FormControl display="flex" alignItems="center" mt={2}>
        <FormLabel htmlFor="data-mappings" mb="0">
          Create data mappings?
        </FormLabel>
        <Switch
          id="data-mappings"
          colorScheme="teal"
          isChecked={mappingEnabled}
          disabled={suggestedDataSources.length === 0}
          onChange={(evt) => {
            onToggleMapping(evt.target.checked);
          }}
        />
      </FormControl>
    </>
  );
};

export default CreatomateDataMappingMagic;
